/* ============================================================================
   10 · PDP prototype — Catarina One-Pocket Scrub Top™ (index.html) and
   Isabel High Waisted Wide-Leg Scrub Pants (pants.html)
   Figma 9Dhvc9N1v5FWO9b54mpCkN · 877:187322 (⭐️ MVP PDP - Scrub Top) ·
   877:187323 (⭐️ MVP PDP - Scrub Pant). One script for both: the page's
   <html data-product> picks the product (data.js PRODUCT); the embroidery
   card runs where it's on the page, the Wear It With cross-sell (xsell.js)
   where that is. Both have the Navy 6XL waitlist (waitlist.js).
   set.html (Figma 1647:80121, Preset Bundles) is a bundle — PRODUCT.kit —
   and swaps the size drawer for the kit drawer (kit.js): every product's
   sizes in one drawer, one ADD for the set. Modules a page doesn't carry
   (Shop the Look, Color Gallery, the waitlist) simply don't start.
   ----------------------------------------------------------------------------
   What's being decided here:

     • The add-to-bag sequence. One CTA, three states: SELECT A SIZE opens the
       size drawer; picking a size confirms in the drawer (tile, title "—XS",
       label) and the drawer closes itself after 450ms — long enough to read
       the confirmation, short enough that nobody reaches for the close button.
       The CTA then morphs to the bag icon + ADD, and ADD is the only spring on
       the page (the affirmative, user-initiated moment the tokens reserve it
       for). ADD puts the real line in the shared bag (src/figs-nav/
       figs-cart.js): the gallery plate flies to the header bag, the badge
       pops as it lands, and the bag opens on it.
     • Whether a PDP can carry the Homepage's reveal vocabulary below the buy
       box without slowing the buy box down. Nothing above the fold waits on a
       tween: title, price, swatches, size and ATB are never pre-hidden; only
       the gallery plates settle.
     • Shop the Look hover on desktop (Figma card 804:127100): the image drops
       to 50% and SELECT SIZE + name—price arrive on --mo-base. Decides whether
       a hover state can replace an always-on label row without hurting
       scannability. Mobile keeps the always-on Mobile·Default card.
     • Quick Buy as a drawer, not a page — the Figma component (1100:87560)
       reused unchanged for every look product.
     • The embroidery module's typewriter, re-created from the GIF on the SITE
       WORKSHOP slide: 18 characters at the GIF's own irregular cadence
       (2.27s), a hold, a 5s loop. It only runs while the card is on screen.
     • Personalize Your FIGS (src/figs-embroidery/, the claude.ai artifact)
       behind ADD EMBROIDERY, and its completed state (Figma 1163:97123):
       EMBROIDERY ADDED in the card's slot, the embroidered plate second in
       the gallery, the charge on the top's bag line. Same on set.html, from
       the kit drawer's ADD EMBROIDERY.

   Reduced motion is a design state: drawers crossfade instead of travelling,
   reveals keep only their opacity, the typewriter shows the full name, the
   fabric tile shows its still frame.
   ========================================================================== */

import {
  mountFigsNav, addToFigsBag, openFigsBag, figsSavedSize, openFigsAccount, isFigsSignedIn,
} from '/src/figs-nav/figs-nav.js';
import {
  animate, inView, stagger, hover, press, MO, state, tween, play, track, teardown,
  setSpeed, clearTracked, SPRING_RELEASE, $, $$, mqDesktop, revealOnView,
} from './mo.js';
import { PRODUCT, LOOK, PICKS, MODELS, SET_XSELL, SIZE_NAMES, money, isSoldOut, isKitSoldOut } from './data.js';
import { renderColors, checkSwatch, markUnavailable, bindRoving, swapText, swapButtonLabel, pulse, announce, garmentHTML } from './ui.js';
import { initSizeDrawer, initQuickBuy } from './drawers.js';
import { initColorGallery, initCompare } from './color-gallery.js';
import { initModelSelector } from './models.js';
import { initSizeChart } from './size-chart.js';
import { initGalleryOverlay } from './overlay.js';
import { initReviews } from './reviews.js';
import { initWaitlist } from './waitlist.js';
import { initCrossSell } from './xsell.js';
import { initKitDrawer, initProductDetails } from './kit.js';
import { renderBundle, syncToggles } from './bundle.js';
import { createFigsEmbroidery, swapSlot, CATARINA_SPIN } from '/src/figs-embroidery/figs-embroidery.js';

/* ============================================================
   HEADER — the shared nav, mounted once (outside boot() so Replay
   never mounts it twice). Its bag is the lab's one bag: whatever was
   added on another prototype is already in it.
   ============================================================ */
mountFigsNav({
  promo: $('[data-fnav-promo]'),
  masthead: $('[data-fnav-masthead]'),
  theme: 'light',
  homeHref: '/experiments/08-d-homepage/',
});

/* The sticky rail parks under the compact bar while it shows — the PLP's
   filter-bar coupling: our own observer on #fnav-masthead with the
   component's exact config, so both flip in the same frame. */
(function initCompactOffset() {
  const compactBar = document.getElementById('fnav-compact-bar');
  const masthead = document.getElementById('fnav-masthead');
  if (!compactBar || !masthead) return;
  let showing = false;
  const sync = () => document.documentElement.style.setProperty(
    '--fnav-compact-offset', showing ? `${compactBar.offsetHeight}px` : '0px');
  new IntersectionObserver(([e]) => { showing = !e.isIntersecting; sync(); }, { threshold: 0 }).observe(masthead);
  addEventListener('resize', sync);
  sync();
})();

/* ============================================================
   STATE
   ============================================================ */
const KIT = PRODUCT.kit ? PRODUCT : null; // a preset bundle (set.html)
const ALL_COLORS = [...PRODUCT.colors.core, ...PRODUCT.colors.limited];
const colorBySlug = (slug) => ALL_COLORS.find((c) => c.slug === slug);
/* the colour: ?color= when the page has it (a set toggle carries it across),
   else the bundle's own (the Espresso Set), else Navy */
const START = [new URLSearchParams(location.search).get('color'), PRODUCT.color, 'navy']
  .find((s) => s && ALL_COLORS.some((c) => c.slug === s)) || ALL_COLORS[0].slug;
const pdp = { color: START, size: null, type: null, picks: null, adding: false }; // size and type (length) are chosen together; a kit's picks are per product
/* a bundle draws its own name, toggles, triptych cards and copy first, so
   the reveals below find them */
if (KIT) renderBundle(KIT, { color: pdp.color });
const TYPE_WORD = PRODUCT.typeLabel || 'Type';
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/* BAG LINES — what an add puts in the shared bag: the product in the page's
   colour, its size and length, and the colour's first photo. A set is one
   line per garment at its live price (the kit's price is their sum). */
const pageUrl = () => `${location.pathname}?color=${pdp.color}`;
/* Embroidery rides on its garment's line as the configurator's bagLine()
   payload — the charges, the garment, the placements — so the bag can list
   it, mark it final sale and reopen it (EDIT); `price` stays the garment's. */
const productLine = () => {
  const c = colorBySlug(pdp.color);
  return {
    id: PRODUCT.id, name: PRODUCT.name, color: c.name, size: pdp.size, type: pdp.type,
    price: PRODUCT.price, embroidery: emb?.bagLine() || null,
    image: c.images?.[0] || c.card, url: pageUrl(),
  };
};
const kitLines = (picks) => KIT.components.map((c) => ({
  id: c.id, name: c.name, color: colorBySlug(pdp.color).name, size: picks[c.id].size, type: picks[c.id].type,
  price: c.price, embroidery: c.embroidery ? emb?.bagLine() || null : null,
  image: c.image(pdp.color),
  url: /^https?:/.test(c.page) ? pageUrl() : `${c.page}?color=${pdp.color}`,
}));
const inBag = (n) => `${n} ${n === 1 ? 'item' : 'items'} in bag.`;

/* ============================================================
   1 · GALLERY — horizontal plates (D 448×597 in a 928 frame,
   M 320×426), scroll-snap, drag on desktop, the Figma progress
   bar. A colour with a single photo (Surprise Me) spans the full
   width instead. Swatches swap the photography with the Color
   Lab's crossfade.
   ============================================================ */
const gal = {
  root: $('[data-gallery]'),
  track: $('[data-gallery-track]'),
  thumb: $('[data-gallery-thumb]'),
  token: 0,
  color: null, // the colour whose photography is on the plates (palette-only colours keep the last)
};

function slidesHTML(color, { settle = false } = {}) {
  const n = color.images.length;
  return color.images.map((src, i) => `
    <figure class="pdp-gallery__slide" aria-roledescription="slide" aria-label="${i + 1} of ${n}"${settle ? ' data-intro' : ''}>
      <button class="pdp-gallery__open" type="button" aria-haspopup="dialog" data-open-overlay="${i}"
        aria-label="View image ${i + 1} of ${n} full screen">
        <img class="pdp-gallery__img" src="${src}" alt="${color.alts?.[i] ?? color.imageAlt ?? `${PRODUCT.plainName} in ${color.name}, image ${i + 1} of ${n}`}"
          ${i > 2 ? 'loading="lazy"' : 'fetchpriority="high"'} decoding="async" draggable="false"${settle ? ' data-settle' : ''}>
      </button>
    </figure>`).join('');
}

function updateProgress() {
  const t = gal.track;
  const max = t.scrollWidth - t.clientWidth;
  const frac = t.scrollWidth ? Math.min(1, t.clientWidth / t.scrollWidth) : 1;
  const w = Math.max(0.12, frac) * 100;
  const p = max > 0 ? t.scrollLeft / max : 0;
  gal.thumb.style.width = `${w}%`;
  gal.thumb.style.transform = `translateX(${((p * (100 - w)) / w) * 100}%)`;
}

function slideStep() {
  const s = gal.track.firstElementChild;
  return s ? s.getBoundingClientRect().width + 4 : gal.track.clientWidth;
}

function initGallery() {
  gal.color = colorBySlug(pdp.color);
  gal.track.innerHTML = slidesHTML(gal.color, { settle: true });
  gal.root.toggleAttribute('data-single', gal.color.images.length === 1);
  syncEmbPlate();
  let raf = 0;
  gal.track.addEventListener('scroll', () => {
    if (!raf) raf = requestAnimationFrame(() => { raf = 0; updateProgress(); });
  }, { passive: true });
  addEventListener('resize', updateProgress);
  updateProgress();

  /* drag-to-scroll for mice; touch and trackpads scroll natively */
  let drag = null;
  let suppressClick = false;
  gal.track.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || !mqDesktop.matches) return;
    drag = { x: e.clientX, left: gal.track.scrollLeft, moved: false, id: e.pointerId };
  });
  gal.track.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) > 4) {
      drag.moved = true;
      gal.track.setPointerCapture(drag.id);
      gal.track.classList.add('is-dragging');
    }
    if (drag.moved) gal.track.scrollLeft = drag.left - dx;
  });
  const endDrag = () => {
    if (!drag) return;
    const moved = drag.moved;
    drag = null;
    if (!moved) return;
    suppressClick = true;
    const step = slideStep();
    const target = Math.round(gal.track.scrollLeft / step) * step;
    gal.track.scrollTo({ left: target, behavior: 'smooth' });
    const release = () => { gal.track.classList.remove('is-dragging'); };
    if ('onscrollend' in window) gal.track.addEventListener('scrollend', release, { once: true });
    else setTimeout(release, 400);
  };
  gal.track.addEventListener('pointerup', endDrag);
  gal.track.addEventListener('pointercancel', endDrag);
  gal.track.addEventListener('click', (e) => {
    if (suppressClick) { e.preventDefault(); e.stopPropagation(); suppressClick = false; }
  }, true);
  /* a plate opens the gallery overlay at that image (a drag never does) */
  gal.track.addEventListener('click', (e) => {
    const b = e.target.closest('[data-open-overlay]');
    if (b) overlay.open(gal.color, Number(b.dataset.openOverlay), b, { name: PRODUCT.plainName });
  });
}

/* Colour change — HP Color Lab recipe: outgoing fades on --mo-base and
   drifts against the direction of travel, incoming arrives on --mo-slow
   from 14px along it, after the new plates have decoded. */
async function swapGallery(color, dir) {
  if (!color?.images) return; // a Figma palette name with no photoshoot
  const token = ++gal.token;
  await play(gal.track, { opacity: [1, 0], x: [0, dir * -10] }, { duration: MO.base, ease: MO.eStandard })?.finished;
  if (token !== gal.token) return;
  gal.color = color;
  gal.track.innerHTML = slidesHTML(color);
  gal.root.toggleAttribute('data-single', color.images.length === 1); // while the plates are faded out
  syncEmbPlate();
  gal.track.scrollLeft = 0;
  await Promise.all($$('img', gal.track).slice(0, 3).map((img) => img.decode?.().catch(() => {})));
  if (token !== gal.token) return;
  updateProgress();
  play(gal.track, { opacity: [0, 1], x: [dir * 14, 0] }, { duration: MO.slow, ease: MO.eEntrance });
}

/* ============================================================
   2 · COLOR VARIANTS
   ============================================================ */
function selectColor(slug) {
  if (slug === pdp.color) return;
  const root = $('[data-colors]');
  const from = ALL_COLORS.findIndex((c) => c.slug === pdp.color);
  const to = ALL_COLORS.findIndex((c) => c.slug === slug);
  pdp.color = slug;
  checkSwatch(root, slug);
  const c = ALL_COLORS[to];
  swapText($('[data-color-name]', root), c.name);
  swapGallery(c, Math.sign(to - from) || 1);
  emb?.setGarment(embGarment());
  syncAvailability();
  xsell?.setColor(c);
  if (KIT) {
    syncToggles(slug);
    if (pdp.picks) swapText(sizeLabel, kitLabelHTML()); // the rows' ghosts follow the colour
    const url = new URL(location.href);
    url.searchParams.set('color', slug);
    history.replaceState(null, '', url);
  }
  announce(`${c.name} selected${soldOut() ? `. ${SIZE_NAMES[pdp.size]} is sold out in ${c.name}` : ''}`);
}

function initColors() {
  const root = $('[data-colors]');
  renderColors(root, PRODUCT.colors, pdp.color, { gallery: Boolean(colorGallery) }); // no COLOR COMPARE on the set (1647:80121)
  $$('.pdp-colors__row, .pdp-colors__group', root).forEach((el) => el.setAttribute('data-intro-form', ''));
  $$('.swatch', root).forEach((el) => el.setAttribute('data-intro-swatch', ''));
  bindRoving(root, (sw) => selectColor(sw.dataset.color));
  $('[data-open-gallery]', root)?.addEventListener('click', (e) => colorGallery.open(e.currentTarget));
}

/* ============================================================
   3 · SIZE → ADD TO BAG
   ============================================================ */
const atbButtons = [$('[data-atb]'), $('[data-atb-sticky]')];
const sizeLabel = $('[data-size-label]');
const sizeMore = $('[data-open-size]');
const form = $('[data-pdp-form]');

/* Navy 6XL is sold out (data.js isSoldOut): picking it is allowed — that's
   how the shopper reaches the waitlist — and the page says so: the label
   gains "—Soldout", the CTA turns into SOLD OUT—NOTIFY ME (Figma PDP/Waitlist
   1126:86609) and the swatch it's sold out in takes Figma's Unavailable
   state (405:24720), while a colour it IS in stays as it was. */
const soldOut = () => !KIT && Boolean(pdp.size) && isSoldOut(pdp.color, pdp.size);
const sizeLabelHTML = () => `${SIZE_NAMES[pdp.size]}<span class="sup sup--variant">${pdp.type}</span>${soldOut() ? '—Soldout' : ''}`;
/* a kit's rows (Figma 1678:96985 · 1678:97421): one per product — its ghost
   in the page's colour, fitted to 40 × 40 in a 40 × 53 Lighter Stone crop
   (the Ghost Image component), then "Top: X-Small^REGULAR" */
const kitLabelHTML = () => KIT.components.map((c) => {
  const p = pdp.picks[c.id];
  return `<span class="pdp-size__line"><span class="pdp-size__ghost" data-role="${c.role.toLowerCase()}"><img src="${c.ghost(pdp.color)}" alt=""></span>`
    + `<span><span class="pdp-size__role">${c.role}: </span>${SIZE_NAMES[p.size] || p.size}<span class="sup sup--variant">${p.type}</span></span></span>`;
}).join('');
const kitSummary = (picks) => KIT.components.map((c) => `${c.shortName} ${SIZE_NAMES[picks[c.id].size]} ${picks[c.id].type}`).join(', ');
const CTA = { select: KIT ? 'Select Size + Add' : 'Select a Size', add: 'Add', notify: 'Sold Out—Notify Me' };
const ctaState = () => (!pdp.size ? 'select' : soldOut() ? 'notify' : 'add');

/* SELECT A SIZE → (bag) ADD → SOLD OUT—NOTIFY ME: the old label leaves, the
   icon (ADD only) and the new label arrive together. */
async function morphCta(btn, next) {
  if (btn.dataset.state === next && !btn.dataset.morphing) return;
  btn.dataset.morphing = next;
  const label = $('[data-atb-label]', btn);
  const icon = $('[data-atb-icon]', btn);
  await play(label, { y: ['0%', '-120%'], opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
  if (btn.dataset.morphing !== next) return; // a later change already owns the button
  delete btn.dataset.morphing;
  btn.dataset.state = next;
  label.textContent = CTA[next];
  play(label, { y: ['120%', '0%'], opacity: [0, 1] }, { duration: MO.slow, ease: MO.eEntrance });
  if (next === 'add') play(icon, { opacity: [0, 1], scale: [0.6, 1] }, { duration: MO.base, ease: MO.eEntrance, delay: 0.06 });
}

/* the swatches, label and CTA follow the current colour × size */
function syncAvailability({ label = true } = {}) {
  const root = $('[data-colors]');
  markUnavailable(root, pdp.size ? ALL_COLORS.filter((c) => isSoldOut(c.slug, pdp.size)).map((c) => c.slug) : [],
    pdp.size ? `sold out in ${SIZE_NAMES[pdp.size]}` : '');
  if (!pdp.size) return;
  if (label) swapText(sizeLabel, sizeLabelHTML());
  atbButtons.forEach((b) => morphCta(b, ctaState()));
}

/* the page takes a size: picked in the drawer (it updates as the drawer
   leaves, so the change is seen, not found), or a signed-in shopper's
   saved size (at once, before the form rises in) */
function takeSize(size, type, { saved = false } = {}) {
  pdp.size = size;
  pdp.type = type;
  xsell?.setPantSize(size);
  form.setAttribute('data-size-selected', '');
  sizeLabel.setAttribute('data-selected', '');
  const show = () => {
    if (sizeMore.hidden) {
      sizeMore.hidden = false;
      play(sizeMore, { opacity: [0, 1], x: [8, 0] }, { duration: MO.base, ease: MO.eEntrance });
    }
    syncAvailability();
    if (soldOut()) announce(`${SIZE_NAMES[size]} ${type} is sold out in ${colorBySlug(pdp.color).name}. Join the waitlist to hear when it's back.`);
  };
  if (saved) show();
  else setTimeout(show, state.reduced ? 0 : 140);
}

const sizeDrawer = KIT ? null : initSizeDrawer({
  product: PRODUCT,
  getColor: () => pdp.color,
  isSoldOut,
  onPick: (size, type) => takeSize(size, type),
  /* both hand back to the size drawer when dismissed (see initSizeDrawer);
     the chart opens on the size marked in the drawer */
  onModels: (link, back) => models.open(link, { onDismiss: back }),
  onSizeChart: (link, back, marked) => sizeChart.open(link, { size: marked.size, onDismiss: back }),
});

/* KIT DRAWER — set.html: the bundle's products in one drawer (kit.js). ADD
   there adds the set (the bag counts each product); as the drawer leaves,
   the page takes the picks — one line per product, CHANGE SIZE, and ADD,
   so another set is one tap. */
const details = KIT ? initProductDetails({ product: KIT, getColor: () => colorBySlug(pdp.color) }) : null;
const kitDrawer = KIT ? initKitDrawer({
  product: KIT,
  getColor: () => pdp.color,
  isSoldOut: isKitSoldOut, // Large, in every colour (data.js)
  /* the drawer confirms the add; the bag opens once the page has taken
     the picks (onCommit), so the drawer, the page and the bag move in turn */
  async onAdd(picks) {
    const n = await addToFigsBag(kitLines(picks), { open: false });
    announce(`Added ${KIT.name}, ${colorBySlug(pdp.color).name}: ${kitSummary(picks)}, to your bag. ${inBag(n)}`);
  },
  /* added: the bag follows; closed on a full set of picks: the page just
     shows them, ready for ADD */
  onCommit(picks, { added = true } = {}) {
    if (added) setTimeout(openFigsBag, state.reduced ? 0 : 420);
    else announce(`Sizes chosen: ${kitSummary(picks)}. Add the set to your bag when you're ready.`);
    takeKitPicks(picks);
  },
  onSizeChart: (link, back, { component, size }) => sizeChart.open(link, { size, chart: component.chart, onDismiss: back }),
  onDetails: (link, back, { component }) => details.open(component, link, { onDismiss: back }),
}) : null;

/* the page takes a set's picks — from the drawer, or a signed-in
   shopper's saved sizes, one per product */
function takeKitPicks(picks) {
  pdp.picks = picks;
  form.setAttribute('data-size-selected', '');
  sizeLabel.setAttribute('data-selected', '');
  if (sizeMore.hidden) {
    sizeMore.hidden = false;
    play(sizeMore, { opacity: [0, 1], x: [8, 0] }, { duration: MO.base, ease: MO.eEntrance });
  }
  swapText(sizeLabel, kitLabelHTML());
  atbButtons.forEach((b) => morphCta(b, 'add'));
  xsell?.setPantSize(picks[KIT.components[0].id].size); // the jacket follows the top's size
}

/* ============================================================
   SAVED SIZES — a signed-in shopper (the nav's log-in flow) arrives with
   the sizes saved in My Account → Preferences already chosen: the page
   shows them, ADD is ready and the size drawer opens on them; a set takes
   one per product, only when every product has one. Signing in on the
   page does the same unless a size is chosen already. A guest picks as
   before.
   ============================================================ */
function takeSavedSizes({ arriving = true } = {}) {
  if (KIT) {
    if (pdp.picks) return;
    const picks = {};
    for (const c of KIT.components) {
      const s = figsSavedSize({ chart: c.chart, role: c.role, gender: c.gender, name: c.plainName, sizes: c.sizes, types: c.types });
      if (!s) return;
      picks[c.id] = { size: s.size, type: s.type || c.types[0] };
    }
    kitDrawer.preset(picks);
    takeKitPicks(picks);
    if (!arriving) announce(`Your saved sizes are selected: ${kitSummary(picks)}.`);
    return;
  }
  if (pdp.size) return;
  const s = figsSavedSize({ chart: PRODUCT.chart, name: PRODUCT.plainName || PRODUCT.name, url: PRODUCT.url, sizes: PRODUCT.sizes, types: PRODUCT.types });
  if (!s) return;
  const type = s.type || PRODUCT.types[0];
  sizeDrawer.preset(s.size, type);
  takeSize(s.size, type, { saved: arriving });
  if (!arriving) announce(`Your saved size is selected: ${SIZE_NAMES[s.size]} ${type}.`);
}
document.addEventListener('figs-account:change', (e) => {
  if (e.detail?.signedIn && !e.detail.sizes) takeSavedSizes({ arriving: false });
});

/* ADD: the CTA confirms on the spring while the gallery plate on screen
   (or, from the sticky bar, the colour's photo) flies to the header bag;
   the bag opens as it lands */
async function addToBag(btn) {
  if (pdp.adding) return;
  pdp.adding = true;
  const c = colorBySlug(pdp.color);
  const summary = KIT
    ? `${KIT.name}, ${c.name}: ${kitSummary(pdp.picks)}`
    : `${PRODUCT.plainName}, ${c.name}, ${SIZE_NAMES[pdp.size]} ${pdp.type}${emb?.has() ? `, with embroidery` : ''}`;
  addToFigsBag(KIT ? kitLines(pdp.picks) : productLine(), { from: [...$$('[data-gallery-track] img'), btn] })
    .then((n) => announce(`Added ${summary}, to your bag. ${inBag(n)}`));
  atbButtons.forEach((b) => {
    pulse(b);
    swapButtonLabel($('[data-atb-label]', b), 'Added to Bag', { affirm: true });
  });
  await wait(state.reduced ? 900 : 1600);
  atbButtons.forEach((b) => swapButtonLabel($('[data-atb-label]', b), CTA[b.dataset.state] || 'Add'));
  pdp.adding = false;
}

/* WAITLIST — Figma 1126:87087 (waitlist.js): the sold-out CTA opens it on
   the exact colour, size and length the shopper picked */
const waitlist = KIT ? null : initWaitlist({ product: PRODUCT });

function initSizeFlow() {
  atbButtons.forEach((btn) => btn.addEventListener('click', () => {
    if (btn.dataset.state === 'notify') {
      const c = colorBySlug(pdp.color);
      waitlist.open(btn, { color: c, size: pdp.size, type: pdp.type, image: c.images?.[0] || c.card });
    } else if (KIT ? !pdp.picks : !pdp.size) (kitDrawer || sizeDrawer).open(btn);
    else if (!pdp.adding) addToBag(btn);
  }));
  sizeMore.addEventListener('click', () => (kitDrawer || sizeDrawer).open(sizeMore));
}

/* ============================================================
   4 · STICKY ATB (mobile) — arrives once the form's own ATB has
   scrolled above the viewport, leaves when it's back.
   ============================================================ */
function initSticky() {
  const sticky = $('[data-sticky]');
  const btn = $('[data-atb-sticky]');
  let shown = false;
  let introDelay = 0.28; // the arrival at load lands with the form's intro, not ahead of the hero
  const set = (on) => {
    if (on === shown) return;
    shown = on;
    sticky.toggleAttribute('data-shown', on);
    sticky.setAttribute('aria-hidden', String(!on));
    btn.tabIndex = on ? 0 : -1;
    play(sticky, on ? { y: ['100%', '0%'], opacity: [0, 1] } : { y: ['0%', '100%'], opacity: [1, 0] },
      on ? { duration: MO.base, ease: MO.eEntrance, delay: introDelay } : { duration: MO.quick, ease: MO.eExit });
  };
  /* wearfigs.com's rule: the bar is up whenever the form's own ATB is off
     screen — below the fold on arrival, or scrolled past — and gone the
     moment any of it is in view, so there's only ever one on screen. Under
     the compact nav bar counts as off screen. A position check, not an
     IntersectionObserver: a fling or an anchor jump can carry the form's ATB
     across the viewport between frames, and the observer would never fire */
  const atb = $('[data-atb]');
  let raf = 0;
  const check = () => {
    raf = 0;
    const r = atb.getBoundingClientRect();
    const top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--fnav-compact-offset')) || 0;
    set(r.top >= innerHeight || r.bottom <= top);
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(check); };
  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue);
  check();
  introDelay = 0;
}

/* ============================================================
   5 · FAVOURITE — the PLP prototype's heart (03): Default / Hover / Filled
   crossfade in CSS; saving a product fires a one-shot obsidian pulse from
   the heart (0.8 → 1.8, 0.6s) and shows the Favorites toast. Removing a
   favourite is silent, as on the PLP.
   ============================================================ */
const favToast = (() => {
  const toast = $('[data-fav-toast]');
  const ring = $('[data-fav-toast-ring]', toast);
  const masthead = document.getElementById('fnav-masthead');
  const SHOW_MS = 6000;
  let shown = false;
  let timer = 0;
  let raf = 0;

  /* 10px under whichever bar is lowest at the top of the viewport: the
     masthead while it's in view, the compact bar once it shows */
  const place = () => {
    raf = 0;
    const compact = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--fnav-compact-offset')) || 0;
    const bottom = Math.max(masthead ? masthead.getBoundingClientRect().bottom : 0, compact);
    toast.style.setProperty('--toast-top', `${Math.max(0, bottom) + 10}px`);
  };
  const onScroll = () => { raf ||= requestAnimationFrame(place); };

  function hide() {
    clearTimeout(timer);
    if (!shown) return;
    shown = false;
    removeEventListener('scroll', onScroll);
    animate(toast, state.reduced ? { opacity: 0 } : { opacity: 0, x: -12 }, { duration: 0.35, ease: MO.eStandard })
      .finished.then(() => { if (!shown) toast.hidden = true; });
  }

  /* one toast only: saving again while it shows restarts its 6s and the
     dot's pulse rather than queuing another */
  function show() {
    clearTimeout(timer);
    place();
    if (!shown) {
      shown = true;
      toast.hidden = false;
      addEventListener('scroll', onScroll, { passive: true });
      animate(toast, state.reduced ? { opacity: [0, 1] } : { opacity: [0, 1], x: [-12, 0] }, { duration: 0.35, ease: MO.eStandard });
    }
    if (!state.reduced) track(animate(ring, { scale: [1, 1.8], opacity: [0.6, 0] }, { duration: 1.1, ease: MO.eStandard, repeat: 2 }));
    timer = setTimeout(hide, SHOW_MS);
  }

  $('[data-fav-toast-close]', toast).addEventListener('click', hide);
  return { show, hide };
})();

function bindFav(btn) {
  const ring = $('[data-fav-pulse]', btn);
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const saved = btn.getAttribute('aria-pressed') !== 'true';
    btn.setAttribute('aria-pressed', String(saved));
    if (!saved) return;
    if (!state.reduced) track(animate(ring, { scale: [0.8, 1.8], opacity: [0.45, 0] }, { duration: 0.6, ease: MO.eStandard }));
    favToast.show();
    announce('Product added to Favorites');
  });
}

/* ============================================================
   6 · SHOP THE LOOK + QUICK BUY
   ============================================================ */
/* ============================================================
   COLOR GALLERY · VISUAL COLOR COMPARE · MODEL SELECTOR ·
   SIZE CHART · GALLERY OVERLAY — color-gallery.js, models.js,
   size-chart.js, overlay.js
   ============================================================ */
const compare = $('[data-drawer="compare"]') ? initCompare({ product: PRODUCT, getColor: () => pdp.color }) : null;
const colorGallery = $('[data-drawer="gallery"]') && initColorGallery({
  product: PRODUCT,
  getColor: () => pdp.color,
  onSelect: selectColor,
  onCompare: () => compare.open(),
});
const models = initModelSelector({ models: MODELS, selected: 'elizabeth' });
const sizeChart = initSizeChart({ product: PRODUCT });
const overlay = initGalleryOverlay();
/* REVIEWS — the page's REVIEWS link opens the reviews drawer (reviews.js) */
const reviews = initReviews({
  product: PRODUCT,
  getColor: () => colorBySlug(pdp.color),
  /* a bundle: one tab per product, in the page's colour */
  tabs: KIT && KIT.components.map((c) => ({ id: c.id, name: c.name, shortName: c.shortName, total: c.reviews, thumb: () => c.image(pdp.color) })),
});
$('[data-open-reviews]').addEventListener('click', (e) => reviews.open(e.currentTarget));
/* the triptych's Fit card switches model, like the size drawer's CHANGE MODEL
   (the set page has no Fit card since slide 23) */
$('[data-tri-switch-model]')?.addEventListener('click', (e) => models.open(e.currentTarget));
/* a kit's product cards open that product's details */
$$('[data-tri-details]').forEach((b) => b.addEventListener('click', () => {
  details.open(KIT.components.find((c) => c.id === b.dataset.triDetails), b);
}));
/* placeholder links (sign in, preferences) stay on the page */
document.addEventListener('click', (e) => { if (e.target.closest('[data-placeholder-link]')) e.preventDefault(); });

/* "Looking for your last size? Sign in here." — the size drawer steps aside
   for the account drawer's Log In; once signed in, the saved size is chosen
   (takeSavedSizes, below) and the line has nothing left to offer. */
const signInLines = $$('.size-drawer__signin');
const syncSignInLines = () => signInLines.forEach((line) => { line.hidden = isFigsSignedIn(); });
syncSignInLines();
document.addEventListener('figs-account:change', syncSignInLines);
$$('.size-drawer__signin-link').forEach((link) => link.addEventListener('click', async () => {
  await Promise.resolve((sizeDrawer?.isOpen && sizeDrawer.close()) || (kitDrawer?.isOpen && kitDrawer.close()));
  openFigsAccount('login');
}));

/* WEAR IT WITH — the Isabel PDP's cross-sell card (xsell.js); the tops
   page has the embroidery card in its place */
const xsell = $('[data-xsell]') ? initCrossSell({
  items: KIT ? SET_XSELL : undefined, // the set page: the Sydney Scrub Jacket
  getColor: () => colorBySlug(pdp.color),
  onSizeChart: (link, back, marked) => sizeChart.open(link, { size: marked.size, chart: marked.chart, onDismiss: back }),
  async onAdd(item, img) {
    const n = await addToFigsBag({
      id: item.id, name: item.fullName, color: item.color, size: item.size, type: item.type,
      price: item.price, image: item.model || item.image, // the bag shows it on model, never the ghost
    }, { from: img });
    announce(`Added ${item.name}, ${item.color}, ${SIZE_NAMES[item.size]}${item.type && item.type !== 'Regular' ? ` ${item.type}` : ''}, to your bag. ${inBag(n)}`);
  },
}) : null;

/* Shop the Look's Quick Buy confirms and closes itself; the bag opens
   once it has gone (onAdded). Its gallery is the look's own colour, so
   that's the photo the bag shows. */
const quickBuy = $('[data-drawer="quickbuy"]') && initQuickBuy({
  async onAdd(product, sel) {
    const color = [...product.colors.core, ...product.colors.limited].find((c) => c.slug === sel.color);
    const n = await addToFigsBag({
      id: product.id, name: product.name, color: color?.name || '', size: sel.size, type: sel.type,
      price: product.price, image: product.gallery[0],
      // a look product with a lab PDP (the Isabel) links back to it from the bag, in the colour bought
      ...(/^https?:/.test(product.url) ? {} : { url: new URL(`${product.url.split('?')[0]}?color=${sel.color}`, location.href).href }),
    }, { open: false });
    announce(`Added ${product.name}, ${color?.name || ''}, ${SIZE_NAMES[sel.size] || sel.size}, to your bag. ${inBag(n)}`);
  },
  onAdded: openFigsBag,
});

function stlCard(p) {
  const color = [...p.colors.core, ...p.colors.limited].find((c) => c.slug === p.color);
  const img = p.imageMobile
    ? `<picture><source media="(max-width: 779px)" srcset="${p.imageMobile}"><img class="stl-card__img stl-card__img--${p.fit}" src="${p.image}" alt="" loading="lazy" decoding="async" data-stl-img></picture>`
    : `<img class="stl-card__img stl-card__img--${p.fit}" src="${p.image}" alt="" loading="lazy" decoding="async" data-stl-img>`;
  return `
    <article class="stl-card" data-reveal data-stl-card>
      <button class="stl-card__hit" type="button" data-qb-open="${p.id}"
        aria-label="Quick buy: ${p.name}, ${color?.name || ''}, ${money(p.price)}">${img}</button>
      <div class="stl-card__info" data-stl-info>
        <p class="stl-card__name">${p.shortName}—${color?.name || ''}</p>
        <p class="stl-card__price">${money(p.price)}</p>
      </div>
      <button class="pdp-link pdp-link--large stl-card__cta" type="button" tabindex="-1" aria-hidden="true" data-qb-open="${p.id}" data-stl-cta>
        <span class="trim">Select Size</span><img src="assets/figma/icon-plus-18.svg" alt="" width="18" height="18">
      </button>
    </article>`;
}

function initStl() {
  const cards = $('[data-stl-cards]');
  const items = KIT ? KIT.look?.items : LOOK; // a bundle brings its own look (or none)
  if (!cards || !items) return;
  cards.innerHTML = items.map(stlCard).join('');
  cards.addEventListener('click', (e) => {
    const t = e.target.closest('[data-qb-open]');
    if (!t) return;
    const p = items.find((x) => x.id === t.dataset.qbOpen);
    /* one size (the tote) comes chosen */
    quickBuy.open(p, t.closest('.stl-card').querySelector('.stl-card__hit'), p.sizes.length === 1 ? { size: p.sizes[0] } : undefined);
  });
}

/* Desktop·Hover (804:128585): image to 50%, SELECT SIZE centred on it,
   name—price row underneath. motion's hover() ignores touch. */
function initStlHover() {
  $$('[data-stl-card]').forEach((card) => {
    const img = $('[data-stl-img]', card);
    const cta = $('[data-stl-cta]', card);
    const info = $('[data-stl-info]', card);
    teardown.push(hover(card, () => {
      if (!mqDesktop.matches) return undefined;
      track(animate(img, { opacity: 0.5 }, { duration: MO.base, ease: MO.eStandard }));
      track(animate(cta, { opacity: 1, y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance }));
      track(animate(info, { opacity: 1, y: [-MO.riseXs / 2, 0] }, { duration: MO.base, ease: MO.eEntrance, delay: 0.04 }));
      return () => {
        const out = [
          track(animate(img, { opacity: 1 }, { duration: MO.quick, ease: MO.eStandard })),
          track(animate([cta, info], { opacity: 0 }, { duration: MO.quick, ease: MO.eStandard })),
        ];
        /* hand the resting state back to CSS so :focus-within still works */
        Promise.all(out.map((a) => a?.finished)).then(() => {
          [img, cta, info].forEach((el) => { el.style.opacity = ''; el.style.transform = ''; });
        }).catch(() => {});
      };
    }));
  });
  /* crossing the breakpoint mid-hover must not strand the desktop hover layer */
  const reset = () => $$('[data-stl-card]').forEach((card) => {
    ['[data-stl-img]', '[data-stl-cta]', '[data-stl-info]'].forEach((s) => {
      const el = $(s, card);
      el.style.opacity = '';
      el.style.transform = '';
    });
  });
  mqDesktop.addEventListener('change', reset);
  teardown.push(() => mqDesktop.removeEventListener('change', reset));
}

/* ============================================================
   7 · PICKS FOR YOU — the Homepage pcard
   ============================================================ */
function pcard(p) {
  return `
    <a class="pcard" href="/experiments/03-plp-prototype/" data-reveal data-pcard>
      <span class="pcard__media">
        <img class="pcard__img" src="${p.image}" alt="${p.name}—${p.colorName}" loading="lazy" decoding="async" data-pcard-img>
        <button class="pcard__fav fav" type="button" aria-label="Save ${p.name} ${p.colorName}" aria-pressed="false" data-pcard-fav>
          <img class="fav__icon fav__icon--default" src="assets/figma/icon-heart-white.svg" alt="" width="20" height="20">
          <img class="fav__icon fav__icon--filled" src="assets/site/icon-heart-filled.svg" alt="" width="20" height="20">
          <span class="fav__pulse" aria-hidden="true" data-fav-pulse></span>
        </button>
      </span>
      <span class="pcard__info">
        <span class="pcard__name">${p.name}&mdash;${p.colorName}</span>
        <span class="pcard__price">${p.price}</span>
      </span>
    </a>`;
}

function initPicks() {
  const rail = $('[data-picks-rail]');
  rail.innerHTML = (KIT ? KIT.picks : PICKS).map(pcard).join('');
  $$('[data-pcard]', rail).forEach((card) => bindFav($('[data-pcard-fav]', card)));
}

function initPicksHover() {
  $$('[data-pcard]').forEach((card) => {
    const img = $('[data-pcard-img]', card);
    teardown.push(hover(card, () => {
      track(animate(img, { scale: MO.scaleHover }, { duration: MO.slow, ease: MO.eEntrance }));
      return () => track(animate(img, { scale: 1 }, { duration: MO.slow, ease: MO.eEntrance }));
    }));
  });
}

/* ============================================================
   8 · FOOTER — Homepage accordion; the row layout (all groups
   open, headers inert) starts at the Homepage footer's 1024
   ============================================================ */
const mqFooterRow = matchMedia('(min-width: 1024px)');
function initFooter() {
  const PLUS = '<path d="M5 1v8M1 5h8"/>';
  const MINUS = '<path d="M1 5h8"/>';
  $$('[data-fgroup]').forEach((group) => {
    const btn = $('[data-fgroup-toggle]', group);
    const list = $('[data-fgroup-list]', group);
    const icon = $('[data-fgroup-icon]', group);
    btn.addEventListener('click', () => {
      if (mqFooterRow.matches) return; // row layout: always open, header inert
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      list.hidden = open;
      icon.innerHTML = open ? PLUS : MINUS;
      if (!open) play(list, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.base, ease: MO.eEntrance });
    });
  });
}

/* ============================================================
   9 · EMBROIDERY TYPEWRITER — re-created from the GIF on the SITE
   WORKSHOP slide (150 frames, 5.0s loop). Each value is the frame
   time a character lands, read off the GIF itself; the two spaces
   fall between their neighbours. Runs on motion's clock, so the
   lab bar's Speed scales it; pauses whenever the card is off screen.
   ============================================================ */
const EMB_TIMES = [130, 230, 370, 500, 630, 770, 870, 1030, 1150, 1270, 1400, 1530, 1630, 1730, 1900, 2015, 2130, 2270];
const EMB_LOOP_MS = 5000;

function initEmbroidery() {
  const el = $('[data-emb-type]');
  const card = $('[data-emb]');
  if (!el || !card) return; // the pants page has the cross-sell here instead
  const text = el.dataset.text || el.textContent.trim();
  el.dataset.text = text;
  if (state.reduced) { el.textContent = text; return; }
  let shown = -1;
  const render = (ms) => {
    let n = 0;
    while (n < EMB_TIMES.length && ms >= EMB_TIMES[n]) n += 1;
    if (n !== shown) { shown = n; el.textContent = text.slice(0, n); }
  };
  render(0);
  const clock = track(animate(0, EMB_LOOP_MS, {
    duration: EMB_LOOP_MS / 1000, ease: 'linear', repeat: Infinity, onUpdate: render,
  }));
  clock.pause();
  teardown.push(inView(card, () => { clock.play(); return () => clock.pause(); }, { amount: 0.5 }));
  teardown.push(() => { clock.stop(); el.textContent = text; });
}

/* ============================================================
   9b · PERSONALIZE YOUR FIGS — the embroidery configurator
   (src/figs-embroidery/, from the claude.ai artifact). ADD
   EMBROIDERY opens it on the top in the page's colour; I'M DONE
   attaches the placements. Then the card becomes EMBROIDERY ADDED
   (Figma 1163:97056; SHOW MORE lists each charge, 2550:172279),
   the gallery gains the embroidered plate as its second image
   (PDP/Embroidery - Added 1163:96298), and ADD puts the top in the
   bag with it. A set carries it in the kit drawer, on its top.
   Review link: ?embroidery opens the configurator on arrival.
   ============================================================ */
/* the top that takes it: a set's embroiderable component, or this page's
   product where the page carries the embroidery card (not the pants page) */
const EMB_TOP = KIT ? KIT.components.find((c) => c.embroidery) : ($('[data-emb]') ? PRODUCT : null);
/* the PDP's ghosts are one 1200 square (or the off-white ghost tinted);
   a set's are cropped to the garment. The navy Catarina also turns in 3D
   on the configurator's placement screen (the module's CATARINA_SPIN). */
function embGarment() {
  const c = colorBySlug(pdp.color);
  const spin = EMB_TOP.id === 'catarina' && pdp.color === 'navy' ? CATARINA_SPIN : null;
  if (KIT) return { src: EMB_TOP.ghost(pdp.color), frame: 'tight', name: c.name, hex: c.hex, spin };
  return { html: garmentHTML(c), frame: 'ghost', name: c.name, hex: c.hex, spin };
}
const emb = EMB_TOP ? createFigsEmbroidery({ garment: embGarment(), onChange: onEmbChange }) : null;

function onEmbChange(attached, { source }) {
  const has = attached.size > 0;
  /* where ADD EMBROIDERY stands, a signed-in shopper (the nav's log-in
     flow) gets the card even with nothing attached: their saved
     preferences, ADD TO PRODUCT · EDIT (the module's User With Preferences) */
  const card = emb.showsCard();
  if (KIT) {
    swapEmb($('[data-kit-emb]'), $('[data-kit-emb-added]'), card); // the kit drawer
    swapEmb(null, $('[data-emb-added]'), has);                      // the completed set, on the page
  } else {
    swapEmb($('[data-emb]'), $('[data-emb-added]'), card);
  }
  syncEmbPlate({ reveal: source === 'done' && has });
  if (source === 'done') announce(has ? `Embroidery added. Total $${emb.total()}.` : 'Embroidery removed.');
  else if (source === 'remove') announce(has ? `Removed. Embroidery total $${emb.total()}.` : 'Embroidery removed.');
}

/* the add-on entry and EMBROIDERY ADDED share one slot (swapSlot: the
   incoming one grows from the outgoing one's height, or from nothing on
   the set page's form, so what's below moves once, smoothly) */
function swapEmb(addEl, addedEl, has) {
  if (!addedEl || has === !addedEl.hidden) return;
  if (has) swapSlot(addEl, addedEl);
  else swapSlot(addedEl, addEl);
}

/* The embroidered plate: the live stage on Figma's blue-grey, the garment
   at 72.5% of the plate, second in the gallery. It arrives the way the
   gallery's plates do (fade on --mo-deliberate, settle from 1.04 on
   --mo-cinematic) and the gallery brings it into view if it's off screen. */
let embPlate = null;
function syncEmbPlate({ reveal = false } = {}) {
  if (!emb) return;
  const want = emb.has() && !gal.root.hasAttribute('data-single');
  const present = embPlate?.fig.isConnected ? embPlate : null;
  if (!want) {
    if (present) { present.preview.destroy(); present.fig.remove(); }
    embPlate = null;
    renumberSlides();
    updateProgress();
    return;
  }
  if (present) return;
  const fig = document.createElement('figure');
  fig.className = 'pdp-gallery__slide pdp-gallery__slide--emb';
  fig.setAttribute('aria-roledescription', 'slide');
  fig.setAttribute('data-emb-plate', '');
  fig.innerHTML = '<div class="femb-plate"><div data-emb-preview></div></div>';
  gal.track.insertBefore(fig, gal.track.children[1] || null);
  embPlate = { fig, preview: emb.preview($('[data-emb-preview]', fig), { fit: 0.725 }) };
  renumberSlides();
  updateProgress();
  if (!reveal) return;
  tween(fig, { opacity: [0, 1] }, { duration: MO.deliberate, ease: MO.eEntrance });
  tween($('.femb-plate', fig), { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance });
  const t = gal.track.getBoundingClientRect();
  const f = fig.getBoundingClientRect();
  if (f.left < t.left || f.right > t.right + 1) {
    gal.track.scrollBy({ left: f.left - t.left, behavior: state.reduced ? 'auto' : 'smooth' });
  }
}
function renumberSlides() {
  const slides = $$('.pdp-gallery__slide', gal.track);
  slides.forEach((el, i) => el.setAttribute('aria-label',
    `${i + 1} of ${slides.length}${el.hasAttribute('data-emb-plate') ? ', your embroidery on the top' : ''}`));
}

function initEmbroideryFlow() {
  if (!emb) return;
  const open = (e) => { e.preventDefault(); emb.open(e.currentTarget); };
  const edit = (btn) => emb.open(btn);
  if (KIT) {
    /* the kit drawer draws the top's ADD EMBROIDERY and its card slot (kit.js);
       the page's form carries the same card once the set is embroidered */
    $$('[data-kit-emb]').forEach((a) => a.addEventListener('click', open));
    const host = $('[data-kit-emb-added]');
    if (host) emb.card(host, { onEdit: edit });
    const page = $('[data-emb-added]');
    if (page) emb.card(page, { onEdit: edit });
  } else {
    $('[data-emb-open]')?.addEventListener('click', open);
    emb.card($('[data-emb-added]'), { onEdit: edit });
  }
  /* signed in on arrival: the preferences card stands in the entry's place
     from the first frame (and rises with the form) */
  if (emb.showsCard()) {
    const [entry, card] = KIT ? [$('[data-kit-emb]'), $('[data-kit-emb-added]')] : [$('[data-emb]'), $('[data-emb-added]')];
    if (entry && card) {
      entry.hidden = true;
      card.hidden = false;
      if (!KIT) card.setAttribute('data-intro-form', '');
    }
  }
  if (new URLSearchParams(location.search).has('embroidery')) {
    setTimeout(() => emb.open(KIT ? null : $('[data-emb-open]')), state.reduced ? 0 : 700);
  }
}

/* ============================================================
   10a · FIRST SCREEN — the load sequence, on the same recipes as
   the Homepage hero and the PLP tagline / grid: copy rises 16px on
   --mo-slow, plates fade and settle from 1.04 (the hero plate), the
   form follows in reading order, the swatches ripple in on the
   Color Lab's .012s stagger. Everything lands inside ~1s. Parts of
   the form below the fold (mobile) reveal when they arrive instead.
   ============================================================ */
let bootAt = 0;
const INTRO = 0.12;
/* a reveal batch that flushes during the load sequence queues behind the
   title; anything revealed later, on scroll, goes straight away */
const introDelay = (d) => () => (performance.now() - bootAt < 500 ? d : 0);

function initIntro() {
  const rise = { y: [MO.riseSm, 0], opacity: [0, 1] };
  tween($$('[data-intro]:not(.pdp-gallery__slide)'), rise,
    { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: INTRO }) });
  tween($('[data-intro-pop]'), { opacity: [0, 1], scale: [0.85, 1] },
    { duration: MO.base, ease: MO.eEntrance, delay: INTRO + 0.16 });

  const slides = $$('.pdp-gallery__slide[data-intro]');
  const plates = slides.map((s) => $('img', s));
  tween(slides, { opacity: [0, 1] }, { duration: MO.deliberate, ease: MO.eEntrance, delay: stagger(MO.stagBase, { startDelay: INTRO }) });
  const settle = tween(plates, { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance, delay: stagger(MO.stagBase, { startDelay: INTRO }) });
  Promise.resolve(settle?.finished).then(() => plates.forEach((p) => p.removeAttribute('data-settle')));

  revealOnView($$('[data-intro-form]'), rise, { duration: MO.slow, startDelay: introDelay(INTRO + 0.2) });
  revealOnView($$('[data-intro-swatch]'), { opacity: [0, 1] }, { duration: MO.base, stag: 0.012, startDelay: introDelay(INTRO + 0.28) });
}

/* Editorial imagery pushes in on hover — the Homepage's collection-tile
   (1.04 on --mo-slow) and split-card (1.04 on --mo-deliberate) plates. */
function initImageHover() {
  const pushIn = (hostSel, imgSel, duration) => $$(hostSel).forEach((host) => {
    const img = $(imgSel, host);
    teardown.push(hover(host, () => {
      track(animate(img, { scale: MO.scaleIn }, { duration, ease: MO.eEntrance }));
      return () => track(animate(img, { scale: 1 }, { duration, ease: MO.eEntrance }));
    }));
  });
  pushIn('[data-tri-card]', '.pdp-tri__img', MO.slow);
  pushIn('[data-edit-large]', '.pdp-edit__large-img', MO.deliberate);
  pushIn('.pdp-edit__card', '.pdp-edit__card-img', MO.deliberate);
}

/* ============================================================
   10 · REVEALS — Homepage recipes, batched row by row by
   revealOnView() (mo.js, shared with the Uniform Builder).
   ============================================================ */
function initReveals() {
  const rise = (d) => ({ y: [d, 0], opacity: [0, 1] });
  const clip = (pct) => ['inset(0 0 ' + pct + ' 0)', 'inset(0 0 0% 0)'];

  /* triptych — collection-tile recipe: rise + clip, copy follows */
  revealOnView($$('[data-tri-card]'), { ...rise(MO.riseMd), clipPath: clip('12%') }, { duration: MO.deliberate, stag: MO.stagBase });
  revealOnView($$('[data-tri-content]'), rise(MO.riseSm), { duration: MO.slow, stag: MO.stagBase, startDelay: 0.24 });

  /* why you'll love this */
  revealOnView($$('[data-love-item]'), rise(MO.riseSm), { duration: MO.slow });
  revealOnView($$('[data-love-copy]'), rise(MO.riseSm), { duration: MO.deliberate, startDelay: 0.08 });
  revealOnView($$('[data-love-cta]'), rise(MO.riseSm), { duration: MO.slow, startDelay: 0.2 });

  /* shop the look — product-row recipe: tight stagger keeps it scannable */
  revealOnView($$('[data-stl-title]'), rise(MO.riseSm), { duration: MO.slow });
  revealOnView($$('[data-stl-look]'), { ...rise(MO.riseMd), clipPath: clip('8%') }, { duration: MO.deliberate });
  revealOnView($$('[data-stl-card]'), rise(MO.riseMd), { duration: MO.deliberate, startDelay: 0.08 });

  /* editorial — split-card recipe for the large plate, loose stagger beside it */
  revealOnView($$('[data-edit-large]'), { ...rise(MO.riseLg), clipPath: clip('8%') }, { duration: MO.cinematic });
  revealOnView($$('[data-edit-item]'), rise(MO.riseMd), { duration: MO.deliberate, stag: MO.stagLoose, startDelay: 0.14 });

  /* picks + footer — exactly the Homepage's. The product row reveals with its
     section, so cards still off-screen in the mobile rail are already there
     when they're swiped in, not fading in mid-swipe. */
  teardown.push(inView($('[data-picks]'), () => {
    tween($$('[data-picks-title]'), rise(MO.riseSm), { duration: MO.slow, ease: MO.eEntrance });
    tween($$('[data-pcard]'), rise(MO.riseMd), { duration: MO.deliberate, delay: stagger(MO.stagTight, { startDelay: 0.08 }), ease: MO.eEntrance });
  }, { amount: 0.15 }));
  teardown.push(inView($('[data-footer]'), () => {
    tween($$('[data-footer-item]'), rise(MO.riseMd), { duration: MO.deliberate, delay: stagger(MO.stagBase), ease: MO.eEntrance });
  }, { amount: 0.1 }));
}

/* Animated plates — the Fabric tile, and a set story's GIF cards — swap to
   their still frame under Reduced. */
function initFabricTile() {
  $$('[data-tri-anim], [data-anim]').forEach((img) => {
    if (!img.dataset.srcAnim) img.dataset.srcAnim = img.getAttribute('src');
    const want = state.reduced ? img.dataset.srcStill : img.dataset.srcAnim;
    if (img.getAttribute('src') !== want) img.setAttribute('src', want);
  });
}

/* ============================================================
   11 · SHARED MICRO-INTERACTIONS — Homepage data-hover-link /
   data-press, plus the ATB press
   ============================================================ */
function initMicro() {
  $$('[data-hover-link]').forEach((l) => {
    teardown.push(hover(l, () => {
      track(animate(l, { y: -2, opacity: 0.82 }, { duration: MO.quick, ease: MO.eEntrance }));
      return () => track(animate(l, { y: 0, opacity: 1 }, { duration: MO.quick, ease: MO.eEntrance }));
    }));
  });
  [...$$('[data-press]'), ...atbButtons, $('[data-qb-atb]'), $('[data-kit-atb]')].filter(Boolean).forEach((el) => {
    teardown.push(press(el, () => {
      track(animate(el, { scale: MO.scalePress }, { duration: MO.instant, ease: MO.eStandard }));
      return () => track(animate(el, { scale: 1 }, state.reduced ? { duration: MO.instant } : SPRING_RELEASE));
    }));
  });
}

/* ============================================================
   LAB BAR — the Homepage's motion.dev toolbar. Speed scales every
   tracked control; Replay / Reduced tear down and rebuild boot().
   On mobile the sticky ATB rides above it.
   ============================================================ */
function initLabBar() {
  const bar = $('[data-lab-bar]');
  const handle = $('[data-lab-handle]');
  const slider = $('#lab-speed');
  const readout = $('[data-readout]');
  const syncOffset = () => {
    const h = !bar.isConnected || bar.hidden || mqDesktop.matches ? 0 : bar.offsetHeight + 8;
    document.documentElement.style.setProperty('--lab-offset', `${h}px`);
  };
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    readout.textContent = `${v.toFixed(2)}x`;
    setSpeed(v);
  });
  const rebuild = () => {
    [sizeDrawer, kitDrawer, details, quickBuy, colorGallery, compare, models, sizeChart, overlay, reviews, waitlist, xsell, emb].forEach((d) => d?.close());
    window.scrollTo({ top: 0, behavior: 'instant' });
    teardown.splice(0).forEach((fn) => { try { fn?.(); } catch (e) { /* ignore */ } });
    clearTracked();
    /* back to the pre-hidden state, so every reveal replays from zero. The
       reset goes through motion too: a fade commits its end value only when
       motion's own record of the element changes, and a record still at 1
       from the last run would leave the element at the inline 0. */
    const pre = $$('[data-reveal], [data-intro], [data-intro-form], [data-intro-pop], [data-intro-swatch], [data-footer-item]');
    pre.forEach((el) => { el.style.opacity = '0'; });
    animate(pre, { opacity: 0 }, { duration: 0 });
    $$('.pdp-gallery__slide[data-intro] img').forEach((img) => img.setAttribute('data-settle', ''));
    requestAnimationFrame(boot);
  };
  $('[data-replay]').addEventListener('click', rebuild);
  const rb = $('[data-reduced-btn]');
  rb.setAttribute('aria-pressed', String(state.reduced));
  rb.addEventListener('click', () => {
    state.reduced = !state.reduced;
    rb.setAttribute('aria-pressed', String(state.reduced));
    rebuild();
  });
  const gb = $('[data-grid-btn]');
  const ov = $('[data-grid-overlay]');
  gb.addEventListener('click', () => {
    const on = gb.getAttribute('aria-pressed') !== 'true';
    gb.setAttribute('aria-pressed', String(on));
    ov.dataset.on = String(on);
  });
  let killed = false;
  $('[data-hide-btn]').addEventListener('click', () => {
    killed = true;
    handle.hidden = true;
    bar.remove();
    syncOffset();
  });
  addEventListener('keydown', (e) => {
    if (killed) return;
    if (e.key.toLowerCase() !== 'h' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(input|textarea|select)$/i.test(e.target.tagName)) return;
    const hide = !bar.hidden;
    bar.hidden = hide;
    handle.hidden = !hide;
    syncOffset();
  });
  handle.addEventListener('click', () => {
    if (killed) return;
    bar.hidden = false;
    handle.hidden = true;
    syncOffset();
  });
  addEventListener('resize', syncOffset);
  mqDesktop.addEventListener('change', syncOffset);
  syncOffset();
  tween(bar, { y: [MO.riseMd, 0], opacity: [0, 1] }, { duration: MO.slow, delay: 0.4, ease: MO.eEntrance });
}

/* ============================================================
   BOOT — one-time bindings first, then the replayable motion layer
   ============================================================ */
function initOnce() {
  initGallery();
  initColors();
  initSizeFlow();
  initSticky();
  bindFav($('[data-fav]'));
  initStl();
  initPicks();
  initFooter();
  initEmbroideryFlow();
  takeSavedSizes();
}

function boot() {
  document.documentElement.dataset.reduced = String(state.reduced);
  bootAt = performance.now();
  initFabricTile();
  initIntro();
  initReveals();
  initEmbroidery();
  initStlHover();
  initPicksHover();
  initImageHover();
  initMicro();
}

initOnce();
boot();
/* tells the head safety net the reveals are running, so .mo-pre stays put
   for the sections that haven't scrolled into view yet */
document.documentElement.setAttribute('data-mo-ready', '');
initLabBar();

addEventListener('load', () => {
  const broken = $$('img').filter((i) => i.getAttribute('src') && i.complete && i.naturalWidth === 0); // a drawer image not filled yet has no src
  if (broken.length) console.error(`[assets] ${broken.length} image(s) failed to load`, broken.map((i) => i.src));
  else console.info(`[assets] ${$$('img').length} images OK`);
});
console.info(`%cFIGS Motion Lab · 10 PDP prototype · ${PRODUCT.shortName}`, 'font-weight:700',
  '\nMotion: motion.dev v12 (vanilla)', '\nReduced:', state.reduced);
